import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MapFilters } from '@/types/map';

// Saved filter preset
export interface SavedFilterPreset {
  id: string;
  name: string;
  filters: MapFilters;
  createdAt: string;
}

// Search history entry
export interface SearchHistoryEntry {
  query: string;
  timestamp: string;
  type: 'address' | 'coordinate' | 'feature';
}

// Recent changes time filter
export type RecentChangesPreset = '1h' | '4h' | '8h' | '24h' | '48h' | '7d' | 'all';

interface MapFiltersState {
  // Filters
  filters: MapFilters;

  // Recent changes time filter
  recentChangesFilter: RecentChangesPreset;

  // Saved filter presets (persisted)
  savedFilterPresets: SavedFilterPreset[];

  // Search history (persisted, max 10)
  searchHistory: SearchHistoryEntry[];

  // Actions
  setFilters: (filters: Partial<MapFilters>) => void;
  clearFilters: () => void;
  saveFilterPreset: (name: string) => void;
  loadFilterPreset: (id: string) => void;
  deleteFilterPreset: (id: string) => void;
  addSearchHistory: (query: string, type: SearchHistoryEntry['type']) => void;
  clearSearchHistory: () => void;
  setRecentChangesFilter: (filter: RecentChangesPreset) => void;
  resetFiltersState: () => void;
}

const INITIAL_FILTERS_STATE = {
  filters: {} as MapFilters,
  recentChangesFilter: 'all' as RecentChangesPreset,
  savedFilterPresets: [] as SavedFilterPreset[],
  searchHistory: [] as SearchHistoryEntry[],
};

export const useMapFiltersStore = create<MapFiltersState>()(
  persist(
    (set) => ({
      ...INITIAL_FILTERS_STATE,

      // Update filters (partial)
      setFilters: (filters) =>
        set((state) => ({
          filters: { ...state.filters, ...filters },
        })),

      // Clear all filters
      clearFilters: () => set({ filters: {} }),

      // Saved filter preset actions
      saveFilterPreset: (name) =>
        set((state) => ({
          savedFilterPresets: [
            ...state.savedFilterPresets,
            { id: crypto.randomUUID(), name, filters: { ...state.filters }, createdAt: new Date().toISOString() },
          ],
        })),
      loadFilterPreset: (id) =>
        set((state) => {
          const preset = state.savedFilterPresets.find((p) => p.id === id);
          if (preset) return { filters: { ...preset.filters } };
          return state;
        }),
      deleteFilterPreset: (id) =>
        set((state) => ({
          savedFilterPresets: state.savedFilterPresets.filter((p) => p.id !== id),
        })),

      // Search history actions
      addSearchHistory: (query, type) =>
        set((state) => {
          const entry: SearchHistoryEntry = { query, timestamp: new Date().toISOString(), type };
          const filtered = state.searchHistory.filter((e) => e.query !== query);
          return { searchHistory: [entry, ...filtered].slice(0, 10) };
        }),
      clearSearchHistory: () => set({ searchHistory: [] }),

      // Recent changes filter actions
      setRecentChangesFilter: (filter) => set({ recentChangesFilter: filter }),

      // Reset filters state to defaults
      resetFiltersState: () => set({ ...INITIAL_FILTERS_STATE }),
    }),
    {
      name: 'map-filters-storage',
      partialize: (state) => ({
        savedFilterPresets: state.savedFilterPresets,
        searchHistory: state.searchHistory,
      }),
    }
  )
);
